import { Injectable } from '@angular/core';
import moment from 'moment';

type ButtonTextConfig = {
	month: string;
	agendaWeek: string;
	today: string;
};

type LocaleDefinition = {
	momentLocale: string;
	aliases?: string[];
	weekdays: string[];
	weekdaysShort: string[];
	months: string[];
	monthsShort: string[];
	buttonText: ButtonTextConfig;
	eventLimitText: string;
};

@Injectable({ providedIn: 'root' })
export default class CalendarLocaleService {
	private initialized = false;

	private readonly localeDefinitions: LocaleDefinition[] = [
		{
			momentLocale: 'ru',
			weekdays: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
			weekdaysShort: ['ВСК', 'ПНД', 'ВТР', 'СРД', 'ЧТВ', 'ПТН', 'СБТ'],
			months: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'],
			monthsShort: ['янв', 'февр', 'март', 'апр', 'май', 'июнь', 'июль', 'авг', 'сент', 'окт', 'ноябрь', 'дек'],
			buttonText: { month: 'месяц', agendaWeek: 'неделя', today: 'сегодня' },
			eventLimitText: 'ещё'
		},
		{
			momentLocale: 'et',
			aliases: ['ee'],
			weekdays: ['Pühapäev', 'Esmaspäev', 'Teisipäev', 'Kolmapäev', 'Neljapäev', 'Reede', 'Laupäev'],
			weekdaysShort: ['P', 'E', 'T', 'K', 'N', 'R', 'L'],
			months: ['jaanuar', 'veebruar', 'märts', 'aprill', 'mai', 'juuni', 'juuli', 'august', 'september', 'oktoober', 'november', 'detsember'],
			monthsShort: ['jaan', 'veebr', 'märts', 'apr', 'mai', 'juuni', 'juuli', 'aug', 'sept', 'okt', 'nov', 'dets'],
			buttonText: { month: 'kuu', agendaWeek: 'nädal', today: 'täna' },
			eventLimitText: 'veel'
		},
		{
			momentLocale: 'fi',
			weekdays: ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'],
			weekdaysShort: ['S', 'M', 'T', 'K', 'T', 'P', 'L'],
			months: ['tammikuu', 'helmikuu', 'maaliskuu', 'huhtikuu', 'toukokuu', 'kesäkuu', 'heinäkuu', 'elokuu', 'syyskuu', 'lokakuu', 'marraskuu', 'joulukuu'],
			monthsShort: ['tam.', 'hel.', 'maa.', 'huh.', 'tou.', 'kes.', 'hei.', 'elo.', 'syys.', 'lok.', 'mar.', 'jou.'],
			buttonText: { month: 'kuukausi', agendaWeek: 'viikko', today: 'tänään' },
			eventLimitText: 'lisää'
		},
		{
			momentLocale: 'en',
			weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
			weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
			months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
			monthsShort: ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May.', 'Jun.', 'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'],
			buttonText: { month: 'month', agendaWeek: 'week', today: 'today' },
			eventLimitText: 'more'
		},
		{
			momentLocale: 'de',
			weekdays: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
			weekdaysShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
			months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
			monthsShort: ['Jan', 'Feb', 'März', 'Apr', 'Mai', 'Juni', 'Juli', 'Aug', 'Sept', 'Okt', 'Nov', 'Dez'],
			buttonText: { month: 'Monat', agendaWeek: 'Woche', today: 'Heute' },
			eventLimitText: 'mehr'
		},
		{
			momentLocale: 'sv',
			weekdays: ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'],
			weekdaysShort: ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'],
			months: ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'],
			monthsShort: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
			buttonText: { month: 'månad', agendaWeek: 'vecka', today: 'idag' },
			eventLimitText: 'fler'
		},
		{
			momentLocale: 'lv',
			weekdays: ['Svētdiena', 'Pirmdiena', 'Otrdiena', 'Trešdiena', 'Ceturtdiena', 'Piektdiena', 'Sestdiena'],
			weekdaysShort: ['Sv', 'Pr', 'Ot', 'Tr', 'Ce', 'Pk', 'Se'],
			months: ['janvāris', 'februāris', 'marts', 'aprīlis', 'maijs', 'jūnijs', 'jūlijs', 'augusts', 'septembris', 'oktobris', 'novembris', 'decembris'],
			monthsShort: ['jan', 'feb', 'mar', 'apr', 'mai', 'jūn', 'jūl', 'aug', 'sep', 'okt', 'nov', 'dec'],
			buttonText: { month: 'mēnesis', agendaWeek: 'nedēļa', today: 'šodien' },
			eventLimitText: 'vairāk'
		},
		{
			momentLocale: 'lt',
			weekdays: ['Sekmadienis', 'Pirmadienis', 'Antradienis', 'Trečiadienis', 'Ketvirtadienis', 'Penktadienis', 'Šeštadienis'],
			weekdaysShort: ['Sk', 'Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št'],
			months: ['sausis', 'vasaris', 'kovas', 'balandis', 'gegužė', 'birželis', 'liepa', 'rugpjūtis', 'rugsėjis', 'spalis', 'lapkritis', 'gruodis'],
			monthsShort: ['sau', 'vas', 'kov', 'bal', 'geg', 'bir', 'liep', 'rgp', 'rgs', 'spa', 'lap', 'gru'],
			buttonText: { month: 'mėnuo', agendaWeek: 'savaitė', today: 'šiandien' },
			eventLimitText: 'daugiau'
		},
		{
			momentLocale: 'es',
			weekdays: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
			weekdaysShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
			months: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
			monthsShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
			buttonText: { month: 'mes', agendaWeek: 'semana', today: 'hoy' },
			eventLimitText: 'más'
		},
		{
			momentLocale: 'fr',
			weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
			weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
			months: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
			monthsShort: ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'],
			buttonText: { month: 'mois', agendaWeek: 'semaine', today: 'aujourd\'hui' },
			eventLimitText: 'plus'
		},
		{
			momentLocale: 'pl',
			weekdays: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
			weekdaysShort: ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb'],
			months: ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'],
			monthsShort: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'],
			buttonText: { month: 'miesiąc', agendaWeek: 'tydzień', today: 'dzisiaj' },
			eventLimitText: 'więcej'
		},
		{
			momentLocale: 'da',
			weekdays: ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'],
			weekdaysShort: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'],
			months: ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'],
			monthsShort: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
			buttonText: { month: 'måned', agendaWeek: 'uge', today: 'i dag' },
			eventLimitText: 'flere'
		},
		{
			momentLocale: 'nb',
			weekdays: ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'],
			weekdaysShort: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'],
			months: ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'],
			monthsShort: ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'],
			buttonText: { month: 'måned', agendaWeek: 'uke', today: 'i dag' },
			eventLimitText: 'flere'
		},
		{
			momentLocale: 'nl',
			weekdays: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
			weekdaysShort: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
			months: ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'],
			monthsShort: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
			buttonText: { month: 'maand', agendaWeek: 'week', today: 'vandaag' },
			eventLimitText: 'meer'
		},
		{
			momentLocale: 'pt',
			weekdays: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
			weekdaysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
			months: ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'],
			monthsShort: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
			buttonText: { month: 'mês', agendaWeek: 'semana', today: 'hoje' },
			eventLimitText: 'mais'
		},
		{
			momentLocale: 'it',
			weekdays: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
			weekdaysShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
			months: ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'],
			monthsShort: ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'],
			buttonText: { month: 'mese', agendaWeek: 'settimana', today: 'oggi' },
			eventLimitText: 'altro'
		}
	];

	initializeMomentLocales() {
		if (this.initialized) {
			return;
		}

		this.localeDefinitions.forEach((definition) => {
			moment.updateLocale(definition.momentLocale, {
				weekdays: definition.weekdays,
				weekdaysShort: definition.weekdaysShort,
				months: definition.months,
				monthsShort: definition.monthsShort
			});

			(definition.aliases || []).forEach((alias) => {
				moment.updateLocale(alias, {
					weekdays: definition.weekdays,
					weekdaysShort: definition.weekdaysShort,
					months: definition.months,
					monthsShort: definition.monthsShort
				});
			});
		});

		(window as any).moment = moment;
		this.initialized = true;
	}

	getFullCalendarLocale(deviceLanguage?: string) {
		this.initializeMomentLocales();

		const localeCode = this.normalizeLocale(deviceLanguage);
		const localeData = moment.localeData(localeCode);
		const definition = this.getLocaleDefinition(localeCode);

		return {
			dayNames: localeData.weekdays(),
			dayNamesShort: localeData.weekdaysShort(),
			monthNames: localeData.months(),
			monthNamesShort: localeData.monthsShort(),
			buttonText: definition.buttonText,
			eventLimitText: definition.eventLimitText
		};
	}

	getMomentLocale(deviceLanguage?: string) {
		this.initializeMomentLocales();
		return this.normalizeLocale(deviceLanguage);
	}

	private normalizeLocale(deviceLanguage?: string) {
		const baseLocale = (deviceLanguage || 'en_GB').split(/[_-]/)[0].toLowerCase();
		return baseLocale === 'ee' ? 'et' : baseLocale;
	}

	private getLocaleDefinition(localeCode: string) {
		return this.localeDefinitions.find((definition) => {
			return definition.momentLocale === localeCode || (definition.aliases || []).includes(localeCode);
		}) || this.localeDefinitions.find((definition) => definition.momentLocale === 'en')!;
	}
}
